import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import {
  DialogBase,
  useSlidesLibraryStoreShallow,
} from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { type KeyboardEventHandler, useCallback, useState } from "react";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { handleAsyncOperation } from "@/shared";
import { useCreateSlidesFolderMutation } from "@/shared/services";

const DIALOG_ID = "create-folder-dialog";

const FolderCreateDialog = ({ activeFolderId }: { activeFolderId: string }) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState("");
  const setFolderId = useSlidesLibraryStoreShallow((s) => s.setFolderId);
  const { mutateAsync: createFolder, isPending: isLoading } =
    useCreateSlidesFolderMutation();

  const handleClose = useCallback(
    () => dispatch(closeDialog(DIALOG_ID)),
    [dispatch],
  );

  const handleSubmit = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName || isLoading) return;
    await handleAsyncOperation({
      operation: async () => {
        const created = await createFolder({
          title: trimmedName,
          parentId: activeFolderId,
        });
        if (created?._id) setFolderId(created._id);
        handleClose();
      },
      onError: () => toast.error("Failed to create folder"),
    });
  }, [createFolder, activeFolderId, handleClose, isLoading, name]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.key === "Enter") handleSubmit();
    },
    [handleSubmit],
  );

  return (
    <DialogBase>
      <DialogBase.Header title="New folder" onClose={handleClose} />
      <DialogBase.Content>
        <span className="tw-mb-1 tw-block tw-font-sans tw-text-[13px] tw-font-semibold">
          Name
        </span>
        <Input
          placeholder="Add your folder name here..."
          value={name}
          onKeyDown={handleKeyDown}
          onChange={(e) => setName(e.target.value)}
        />
      </DialogBase.Content>
      <DialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!name?.trim()}
          className={cn(isLoading && "tw-cursor-not-allowed")}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Adding...
            </>
          ) : (
            "Add folder"
          )}
        </Button>
      </DialogBase.Footer>
    </DialogBase>
  );
};

FolderCreateDialog.id = DIALOG_ID;
export { FolderCreateDialog };
